import { useCallback, useEffect, useRef } from 'react';
import { NotificationService } from 'services/notificationService/NotificatioinService';
import { useAppDispatch } from 'store/hooks';
import { API_CASHIER_SERVICE } from 'utils/constants';
import { useGetSessionId } from 'utils/session';

import type { ReservationCardStatus } from '../types';

/**
 * WebSocket subscription for card verification results from KYCaid.
 *
 * Listens to the `/verifications` hub for `KycVerificationResult` messages. The backend pushes
 * the final verification status here once KYCaid has processed the uploaded document.
 *
 * Constants are duplicated from `services/notificationService/constants` on purpose: this feature
 * must stay deletable without touching shared code.
 */
const VERIFICATION_URL = '/verifications';
const VERIFICATION_TARGET = 'KycVerificationResult';

interface VerificationMessage {
  user_id: number;
  user_card_id: number;
  kyc_verification_status: ReservationCardStatus;
}

interface UseReservationVerificationSocketProps {
  cardId: number | null;
  onResult: (status: ReservationCardStatus, cardId: number) => void;
}

export const useReservationVerificationSocket = ({ cardId, onResult }: UseReservationVerificationSocketProps) => {
  const dispatch = useAppDispatch();
  const sessionId = useGetSessionId();
  const serviceRef = useRef<NotificationService | null>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const cardIdRef = useRef(cardId);
  cardIdRef.current = cardId;

  const disconnect = useCallback(async () => {
    if (serviceRef.current) {
      await serviceRef.current.stop(VERIFICATION_TARGET);
      serviceRef.current = null;
    }
  }, []);

  const connect = useCallback(async () => {
    if (!sessionId) return;

    if (serviceRef.current) {
      await disconnect();
    }

    const service = new NotificationService(sessionId, dispatch, `${API_CASHIER_SERVICE}${VERIFICATION_URL}`);
    serviceRef.current = service;

    try {
      await service.start();
      service.subscribe({
        name: VERIFICATION_TARGET,
        callback: (json: string, unsubscribe: () => void) => {
          let data: VerificationMessage;

          try {
            data = JSON.parse(json) as VerificationMessage;
          } catch {
            return;
          }

          // Ignore messages for other cards
          if (cardIdRef.current && data.user_card_id !== cardIdRef.current) {
            return;
          }

          onResultRef.current(data.kyc_verification_status, data.user_card_id);
          unsubscribe();
        },
      });
    } catch {
      serviceRef.current = null;
    }
  }, [sessionId, dispatch, disconnect]);

  useEffect(
    () => () => {
      disconnect();
    },
    [disconnect],
  );

  return { connect, disconnect };
};
